// ─── src/hooks/useThemeColors.js ─────────────────────────────────────────────
//
// Fetches the saved theme colors from Firestore (theme/colors document)
// and returns them as an array of { varName, hex, label } objects.
//
// Used by admin pages (CertificatesPage, ProjectsPage, SkillsPage, etc.)
// to populate color dropdowns with the live portfolio palette.
//
// Falls back to CSS defaults if no theme doc exists in Firestore yet.

import { useState, useEffect } from 'react';
import { themeService } from '../firebase/services';

// These must match your index.css :root defaults exactly
export const CSS_DEFAULTS = {
  '--black':  '#050505',
  '--white':  '#f0ece4',
  '--acid':   '#c8f04a',
  '--cyan':   '#00e5ff',
  '--muted':  '#2a2a2a',
  '--border': '#1e1e1e',
};

const VAR_LABELS = {
  '--black':  'Background',
  '--white':  'Foreground',
  '--acid':   'Acid Green',
  '--cyan':   'Cyan',
  '--muted':  'Surface',
  '--border': 'Border',
};

// ── Hook ──────────────────────────────────────────────────────────────────────
// Returns:
//   themeColors  — array of { varName, hex, label } from Firestore (or defaults)
//   loading      — true while fetching
//   error        — error message string or null

export function useThemeColors() {
  const [themeColors, setThemeColors] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    themeService.get()
      .then(data => {
        if (cancelled) return;
        const source = data ? { ...CSS_DEFAULTS, ...data } : { ...CSS_DEFAULTS };
        const colors = Object.entries(source).map(([varName, hex]) => ({
          varName,
          hex,
          label: VAR_LABELS[varName] || varName,
        }));
        setThemeColors(colors);
        setError(null);
      })
      .catch(e => {
        if (cancelled) return;
        // Non-fatal: fall back to CSS defaults
        const colors = Object.entries(CSS_DEFAULTS).map(([varName, hex]) => ({
          varName,
          hex,
          label: VAR_LABELS[varName] || varName,
        }));
        setThemeColors(colors);
        setError('Could not load theme from Firestore — using CSS defaults.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, []);

  return { themeColors, loading, error };
}